/**
 * Working days in England and Wales: Monday to Friday, less bank holidays (the engine's list,
 * @ryoma/engine workdays.ts, so the report due date and reply times agree).
 * Used for the reply time promised on the website (2 working days).
 */
import { ENGLAND_WALES_BANK_HOLIDAYS } from "@ryoma/engine";

const BANK_HOLIDAYS = new Set(ENGLAND_WALES_BANK_HOLIDAYS);
/** The last year the bank holiday list covers. */
export const BANK_HOLIDAYS_UNTIL = `${[...ENGLAND_WALES_BANK_HOLIDAYS].sort().at(-1)!.slice(0, 4)}-12-31`;

const DAY = 864e5;
const isoDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/** Today's date in London, as YYYY-MM-DD. */
export function londonDate(at: Date): string {
  return at.toLocaleDateString("en-CA", { timeZone: "Europe/London" });
}

export function isWorkingDay(iso: string): boolean {
  const dow = new Date(`${iso}T12:00:00Z`).getUTCDay();
  return dow !== 0 && dow !== 6 && !BANK_HOLIDAYS.has(iso);
}

/** The date n working days after the given date (n = 0 gives the next working day on or after it). */
export function addWorkingDays(iso: string, n: number): string {
  let ms = Date.parse(`${iso}T12:00:00Z`);
  while (!isWorkingDay(isoDay(ms))) ms += DAY;
  let left = n;
  while (left > 0) {
    ms += DAY;
    if (isWorkingDay(isoDay(ms))) left--;
  }
  return isoDay(ms);
}

/** Working days from a to b: positive when b is later, 0 on the same day. */
export function workingDaysBetween(a: string, b: string): number {
  const sign = b >= a ? 1 : -1;
  const [from, to] = sign > 0 ? [a, b] : [b, a];
  let n = 0;
  let ms = Date.parse(`${from}T12:00:00Z`);
  const end = Date.parse(`${to}T12:00:00Z`);
  while (ms < end) {
    ms += DAY;
    if (isWorkingDay(isoDay(ms))) n++;
  }
  return sign * n;
}

/**
 * The reply deadline for something received at a given moment: 5.30pm London time,
 * a number of working days after the day it arrived.
 */
export function replyDue(received: Date, workingDays: number): Date {
  // Something that arrives on a weekend or bank holiday counts from the next working day.
  const day = addWorkingDays(londonDate(received), workingDays);
  // 17:30 in London: find the UTC offset for that date.
  const noonUtc = new Date(`${day}T12:00:00Z`);
  const londonNoon = Number(noonUtc.toLocaleString("en-GB", { timeZone: "Europe/London", hour: "2-digit", hour12: false }));
  const offsetHours = londonNoon - 12;
  return new Date(Date.parse(`${day}T17:30:00Z`) - offsetHours * 3600e3);
}
